// frontend/src/pages/KRXIdxVsTradePage.js

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../services/api';
import ReactECharts from 'echarts-for-react';
import useDateRange from '../../hooks/useDateRange';
import DateRangeControls from '../../components/DateRangeControls';
import DataTransformControls from '../../components/DataTransformControls';
import { createChartOptions } from '../../utils/chartOptions';
import { Checkbox, Typography, Card, Divider, Select, Space } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import ResponsiveTypography from '../../components/ResponsiveTypography';

import Loading from '../../components/Loading';

const { Title } = ResponsiveTypography;
const { Text } = Typography;

const KRXIdxVsTradePage = () => {
    const [selectedAsset, setSelectedAsset] = useState('KOSPI200');
    // 지표 선택 state 제거
    const [isShifted, setIsShifted] = useState(true);
    const [dataType, setDataType] = useState('Value');
    const [periodType, setPeriodType] = useState('TTM');
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);

    const { minDate, maxDate, viewStartDate, viewEndDate, handlePeriodChange, handleDateChange, isDateReady } = useDateRange(chartData?.krx_idx_data, { dateColumn: 'Date', defaultPeriod: '10Y' });

    useEffect(() => {
        setLoading(true);
        apiClient.get('/krx-idx-vs-trade', {
            params: { 
                asset_name: selectedAsset,
                shift_days: isShifted, 
                data_type: dataType.toLowerCase(), 
                period_type: periodType.toLowerCase() 
            }
        }).then(res => { setChartData(res.data); setLoading(false); })
        .catch(error => {
            console.error("Failed to fetch chart data:", error);
            setChartData(null);
            setLoading(false);
        });
    }, [selectedAsset, isShifted, dataType, periodType]);

    const getChartOption = useCallback(() => {
        if (!isDateReady || !chartData) return {};
    
        const tradeUnit = dataType === 'Value' ? '$1B' : '%';
    
        const finalOptions = {
            legend: [
                { data: [selectedAsset, `${selectedAsset}/USDKRW`], top: '2%' },
                { data: ['수출', '수입', '무역수지'], top: '44%' }
            ],
            // 오른쪽 Y축 공간 확보를 위해 right 마진 조정
            grid: [
                { top: '8%', height: '30%', left: '55rem', right: '55rem'},
                { top: '55%', height: '30%', left: '55rem', right: '55rem' }
            ],
            xAxis: [
                { type: 'time', gridIndex: 0, axisLabel: { show: false } },
                { type: 'time', gridIndex: 1 }
            ],
            yAxis: [
                // 0: KOSPI 차트 Y축 (왼쪽)

                { type: 'value', 
                    name: '', gridIndex: 0, scale: true, log: true, axisLine: { show: true } 
                },
                // 1: 수출입 차트 Y축 (왼쪽)
                {
                    type: 'value',
                    name: `수출입 (${tradeUnit})`,
                    gridIndex: 1,
                    left: '20rem',
                    scale: true,
                    splitLine: { show: false },
                    axisLine: { show: true } 

                },
                // 2: 무역수지 차트 Y축 (오른쪽)
                {
                    type: 'value',
                    name: `무역수지 (${tradeUnit})    `,
                    gridIndex: 1,
                    right: '20rem',
                    scale: true,
                    splitLine: { show: false },
                    axisLine: { show: true } 
                    
                }
            ],
            series: [
                // KOSPI 시리즈 (yAxisIndex: 0)
                { name: selectedAsset, type: 'line', data: chartData.krx_idx_data.map(d => [d.Date, d.Index]), xAxisIndex: 0, yAxisIndex: 0 },
                { name: `${selectedAsset}/USDKRW`, type: 'line', data: chartData.krx_idx_data.map(d => [d.Date, d.Index_USDKRW]), xAxisIndex: 0, yAxisIndex: 0 },
                
                // 수출 시리즈 (yAxisIndex: 1 - 왼쪽 축)
                { name: '수출', type: 'line', data: chartData.trade_data.map(d => [d.Date, d.수출]), xAxisIndex: 1, yAxisIndex: 1 },
                // 수입 시리즈 (yAxisIndex: 1 - 왼쪽 축)
                { name: '수입', type: 'line', data: chartData.trade_data.map(d => [d.Date, d.수입]), xAxisIndex: 1, yAxisIndex: 1 },
                // 무역수지 시리즈 (yAxisIndex: 2 - 오른쪽 축)
                { name: '무역수지', type: 'line', data: chartData.trade_data.map(d => [d.Date, d.무역수지]), xAxisIndex: 1, yAxisIndex: 2 }
            ],
            dataZoom: [
                { type: 'slider', show: true, xAxisIndex: [0, 1], bottom: '3%', startValue: viewStartDate?.toISOString(), endValue: viewEndDate?.toISOString() },
                { type: 'inside', xAxisIndex: [0, 1] }
            ],
        };

    
        return createChartOptions(finalOptions);
    
    }, [isDateReady, chartData, dataType, viewStartDate, viewEndDate, selectedAsset]);

    if (loading || !isDateReady) {
        return <Loading />;
    }

    return (
        <div className="content-box">
            <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>
                주가지수 vs 수출입 동향
            </Title>

            <Card style={{ marginTop: '1rem', padding: '0.625rem' }} bodyStyle={{ padding: '0rem'}}>
                    <Space wrap>
                        <Text 
                            strong
                            style={{ 
                                fontSize: 'inherit',
                                transition: 'font-size 0.2s ease-in-out'
                            }}
                        >
                            <SettingOutlined /> 주가지수 
                        </Text>
                        <Select
                            value={selectedAsset}
                            onChange={setSelectedAsset}
                            style={{ width: 150 }}
                            size='small'
                            options={[
                                { value: 'KOSPI', label: 'KOSPI' },
                                { value: 'KOSPI200', label: 'KOSPI200' },
                                { value: 'KOSDAQ', label: 'KOSDAQ' }
                            ]}
                        />
                        {/* 지표 선택 메뉴 제거됨 */}
                    </Space>
            </Card>

            <Card style={{marginTop: '0.75rem', padding: '0.625rem'}} bodyStyle={{ padding: '0rem' }}>
                <Title 
                    level={4} 
                    style={{ 
                        marginLeft: '0.3125rem',
                    }}
                >
                    {selectedAsset} vs 수출입 동향
                    
                </Title>
                <Divider style={{ margin: '0.625rem 0 0 0' }} />

                <div style={{
                    display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem',
                    marginBottom: '0.625rem', marginTop: '0.625rem', marginLeft: '0.625rem', marginRight: '0.625rem'
                }}>
                    <Text > <SettingOutlined /> 수출입 데이터</Text>

                    <DataTransformControls 
                        dataType={dataType} 
                        periodType={periodType} 
                        onDataTypeChange={setDataType} 
                        onPeriodTypeChange={setPeriodType} 
                        size='small' 
                    />
                </div>

                <ReactECharts option={getChartOption()} style={{ height: '31.25rem' }} showLoading={loading} />
                
                <div style={{ 
                    display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', 
                    marginTop: '1rem', marginBottom: '1rem', marginLeft: '1.0rem', marginRight: '1.0rem' }}>
                    <Checkbox checked={isShifted} onChange={e => setIsShifted(e.target.checked)}>수출입 데이터 15일 시프트</Checkbox>
                </div>
                <Divider style={{ marginTop: '0.3125rem', marginBottom: '1.25rem' }} />
                
                <DateRangeControls 
                    minDate={minDate} 
                    maxDate={maxDate} 
                    viewStartDate={viewStartDate} 
                    viewEndDate={viewEndDate} 
                    onPeriodChange={handlePeriodChange} 
                    onDateChange={handleDateChange} 
                />
            </Card>
        </div>
    );
};
export default KRXIdxVsTradePage;